import { CheckCircle2, Info } from "lucide-react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import { ClientsService } from "../../services/clientsService";
import type { ClientAccount, CreateClientAccountRequest } from "../../types/clients-api.types";
import { canWriteClientAccounts } from "../../utils/clientsPermissions";
import { ClientAccountCreateForm } from "../accounts/ClientAccountCreateForm";
import { KycLinkReveal } from "../kyc/KycLinkReveal";
import type { IssuedKycLink } from "./OrganizationSuccessStep";

/** Summary of the client just created — enough for the caller to select it without refetching. */
export interface CreatedClientSummary {
  id: string;
  identityId: string | null;
  displayName: string;
}

export interface ClientSuccessStepProps {
  client: CreatedClientSummary;
  /** KYC link issued best-effort right after create; null renders the pending-verification note instead. */
  kycLink: IssuedKycLink | null;
  /** When set (dispersions shortcut), offers registering an account right away, locked to this currency. */
  accountCurrency?: string;
  /**
   * Seeds that account form with a destination already known — the dispersions "save this recipient" action,
   * which runs after the money went out and should not ask for the key it just paid.
   */
  accountPrefill?: { bank: string; methodFields: Record<string, string> };
  onAccountCreated?: (account: ClientAccount) => void;
  /** Lets the host drawer block closing while the account create is in flight. */
  onSubmittingChange?: (submitting: boolean) => void;
}

/**
 * Final screen of the client wizard when the caller wants to keep working with the new client (dispersions
 * shortcut) — mirror of `OrganizationSuccessStep`: confirmation banner + shareable KYC link, plus the
 * optional inline account form so the client is usable in the dispersion immediately.
 */
export function ClientSuccessStep({
  client,
  kycLink,
  accountCurrency,
  accountPrefill,
  onAccountCreated,
  onSubmittingChange,
}: ClientSuccessStepProps) {
  const { t } = useTranslations("clients");
  const { user } = useAuth();
  const offerAccount = !!accountCurrency && canWriteClientAccounts(user);

  return (
    <div className="space-y-4">
      <ColocaAlert
        variant="success"
        icon={CheckCircle2}
        text={t("client_success_text", { name: client.displayName })}
      />
      {kycLink ? (
        <KycLinkReveal link={kycLink.url} expiresAt={kycLink.expiresAt} />
      ) : (
        <ColocaAlert variant="secondary" icon={Info} text={t("client_success_kyc_pending")} />
      )}
      {offerAccount && (
        <section className="border-t border-border pt-4">
          <h3 className="text-sm font-semibold text-foreground">
            {t("client_success_account_heading", { currency: accountCurrency })}
          </h3>
          <p className="mt-1 mb-4 text-[13px] text-muted-foreground">{t("client_success_account_hint")}</p>
          <ClientAccountCreateForm
            createAccount={async (body: CreateClientAccountRequest) => {
              const { data } = await ClientsService.createAccount(client.id, body);
              return data;
            }}
            onCreated={onAccountCreated}
            defaultCurrency={accountCurrency}
            lockCurrency
            defaultBank={accountPrefill?.bank}
            defaultMethodFields={accountPrefill?.methodFields}
            onSubmittingChange={onSubmittingChange}
          />
        </section>
      )}
    </div>
  );
}
